import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableHead, Toolbar, Typography,
    CircularProgress, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Chip, Autocomplete
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import PublicIcon from "@mui/icons-material/Public";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import {
    getMarketingItems,
    getMarketingItemDetails,
    updateMarketingItem,
    createMarketingItem,
    deleteMarketingItem,
    uploadMarketingImage,
    uploadNewMarketingImage
} from "../services/marketingService";
import { getAllStates, getCitiesByState } from "../services/referenceService";
import {
    StyledTableContainer,
    StyledTableCell,
    StyledTableRow,
    StyledPaper,
} from "../assets/styles/tableStyle";
import FancyContextMenu, { ContextMenuAction } from "../components/FancyContextMenu";

type MarketingItem = {
    id: number;
    imageUrl: string;
    title: string;
    subtitle: string;
    city?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

// Helper option type for Autocomplete
type CityOption = {
    label: string;
    value: string | null; // null represents GLOBAL
};

const MarketingList: React.FC = () => {
    const [items, setItems] = useState<MarketingItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);

    // Reference Data State
    const [statesList, setStatesList] = useState<any[]>([]);
    const [citiesList, setCitiesList] = useState<any[]>([]);
    const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

    // Edit/Create modal state
    const [detailOpen, setDetailOpen] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [selectedItem, setSelectedItem] = useState<MarketingItem | null>(null);
    const [editData, setEditData] = useState<Partial<MarketingItem>>({});
    const [editLoading, setEditLoading] = useState<boolean>(false);
    const [modalMode, setModalMode] = useState<"edit" | "create">("edit");
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

    const [uploading, setUploading] = useState<boolean>(false);

    // Context menu management
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);
    const [selectedRow, setSelectedRow] = useState<MarketingItem | null>(null);

    useEffect(() => {
        fetchItems();
        loadStates();
    }, []);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await getMarketingItems();
            setItems(res.data.items);
        } finally {
            setLoading(false);
        }
    };

    const loadStates = async () => {
        try {
            const res = await getAllStates();
            if (res.data.success) {
                setStatesList(res.data.states);
            }
        } catch (e) { console.error(e); }
    };

    const handleStateChange = async (stateCode: string | null) => {
        setSelectedStateCode(stateCode);
        setCitiesList([]);

        // When state changes, we don't necessarily clear the city in Edit mode
        // unless the user explicitly picks a new state to find a new city.
        // For smoother UX, we can leave the current city selected until they pick a new one.

        if (stateCode) {
            try {
                const res = await getCitiesByState(stateCode);
                if (res.data.success) {
                    setCitiesList(res.data.cities);
                }
            } catch (e) { console.error(e); }
        }
    };

    // Modal logic
    const openCreateModal = () => {
        setModalMode("create");
        setEditData({ city: undefined });
        setSelectedStateCode(null);
        setCitiesList([]);
        setImageFile(null);
        setSelectedId(null);
        setDetailOpen(true);
    };

    const handleRowClick = async (id: number) => {
        setModalMode("edit");
        setSelectedId(id);
        setDetailOpen(true);
        setEditLoading(true);
        setImageFile(null);
        setSelectedStateCode(null);
        setCitiesList([]);
        try {
            const res = await getMarketingItemDetails(id);
            setSelectedItem(res.data);
            setEditData(res.data);
        } finally {
            setEditLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setDetailOpen(false);
        setSelectedId(null);
        setSelectedItem(null);
        setEditData({});
        setImageFile(null);
    };

    const handleFieldChange = (field: keyof MarketingItem, value: any) => {
        setEditData((prev) => ({ ...prev, [field]: value }));
    };

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);
            setImagePreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSaveEdit = async () => {
        setEditLoading(true);
        try {
            let imageUrl = editData.imageUrl;

            // 1. First, handle image upload (if an image is selected)
            let uploadRes: any = null;
            if (imageFile) {
                setUploading(true);
                if (modalMode === 'create') {
                    uploadRes = await uploadNewMarketingImage(imageFile);
                } else if (modalMode === 'edit' && selectedId) {
                    uploadRes = await uploadMarketingImage(selectedId, imageFile);
                }
                setUploading(false);

                if (uploadRes && uploadRes.data && uploadRes.data.imageUrl) {
                    imageUrl = uploadRes.data.imageUrl;
                }
            }

            // If user left it totally empty (undefined), force them to choose?
            // Or default to Global?
            // Let's enforce selection since we made it empty by default.
            if (editData.city === undefined && modalMode === 'create') {
                alert("Please select a Target Audience (Global or Specific City).");
                setEditLoading(false);
                return;
            }

            // Map DB value: undefined or null -> null.
            const cityPayload = editData.city || null;

            if (modalMode === "create") {
                // Ensure imageUrl is present for schema!
                if (!imageUrl) {
                    // Show user error or use a placeholder/default
                    alert("Image is required for creating a marketing item.");
                    return;
                }
                const { title, subtitle } = editData;
                await createMarketingItem({
                    title,
                    subtitle,
                    imageUrl,
                    city: cityPayload
                });
            } else if (modalMode === "edit" && selectedId) {
                // Merge new/old imageUrl and save
                const { title, subtitle } = editData;
                await updateMarketingItem(selectedId, {
                    title,
                    subtitle,
                    imageUrl,
                    city: cityPayload
                });
            }

            await fetchItems();
            setDetailOpen(false);
        } finally {
            setEditLoading(false);
            setUploading(false);
            setImageFile(null);
            setImagePreviewUrl(null);
        }
    };

    // Context menu logic
    const handleContextMenu = (event: React.MouseEvent, row: MarketingItem) => {
        event.preventDefault();
        setSelectedRow(row);
        setMenu({ top: event.clientY, left: event.clientX });
    };

    const handleCloseMenu = () => setMenu(null);

    const handleEditContext = () => {
        if (selectedRow) { handleRowClick(selectedRow.id); }
        handleCloseMenu();
    };

    const handleDeleteContext = async () => {
        if (selectedRow) {
            setLoading(true);
            try {
                await deleteMarketingItem(selectedRow.id);
                await fetchItems();
            } finally {
                setLoading(false);
            }
        }
        handleCloseMenu();
    };

    const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const actions: ContextMenuAction[] = [
        {
            dividerBefore: true,
            label: "Edit",
            icon: <EditIcon color="info" fontSize="small" />,
            onClick: handleEditContext,
            color: "info",
        },
        {
            label: "Delete",
            icon: <DeleteIcon color="error" fontSize="small" />,
            onClick: handleDeleteContext,
            color: "error",
            dividerBefore: true,
        }
    ];

    // Prepare City Options for Autocomplete
    const globalOption: CityOption = { label: "Global / All Cities", value: null };
    const cityOptions: CityOption[] = [
        globalOption,
        ...citiesList.map(c => ({ label: c.name, value: c.name }))
    ];

    // Determine current value for City Autocomplete
    // 1. If editData.city is undefined (Create mode start) -> value is null (renders empty)
    // 2. If editData.city is null (Edit mode Global) -> value is globalOption
    // 3. If editData.city is "Bangalore" -> find option or create temp option
    let currentCityValue: CityOption | null = null;

    if (editData.city === null) {
        currentCityValue = globalOption;
    } else if (typeof editData.city === 'string') {
        // Try to find in loaded list, or create object to display it
        const found = cityOptions.find(o => o.value === editData.city);
        currentCityValue = found || { label: editData.city, value: editData.city };
    }
    // if undefined, currentCityValue remains null -> Autocomplete shows empty

    return (
        <StyledPaper sx={{ maxWidth: "98vw", width: "100%", margin: '0 auto', my: 2 }}>
            <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="h6">Marketing Items</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openCreateModal}
                    sx={{ ml: 2 }}
                >
                    Add New
                </Button>
            </Toolbar>
            {loading ? (
                <CircularProgress sx={{ margin: 2 }} />
            ) : (
                <StyledTableContainer>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>ID</StyledTableCell>
                                <StyledTableCell>Target City</StyledTableCell>
                                <StyledTableCell>Image</StyledTableCell>
                                <StyledTableCell>Title</StyledTableCell>
                                <StyledTableCell>Subtitle</StyledTableCell>
                                <StyledTableCell>Created At</StyledTableCell>
                                <StyledTableCell>Updated At</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.map((item) => (
                                <StyledTableRow
                                    key={item.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => handleRowClick(item.id)}
                                    onContextMenu={e => handleContextMenu(e, item)}
                                >
                                    <StyledTableCell>{item.id}</StyledTableCell>
                                    <StyledTableCell>
                                        {item.city ? (
                                            <Chip
                                                icon={<LocationCityIcon style={{color: 'white'}} />}
                                                label={item.city}
                                                size="small"
                                                color="primary"
                                            />
                                        ) : (
                                            <Chip
                                                icon={<PublicIcon style={{color: 'white'}} />}
                                                label="GLOBAL"
                                                size="small"
                                                color="success"
                                            />
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt="carousel" style={{ maxHeight: 32, borderRadius: 6 }} />
                                        ) : (
                                            <ImageIcon color="disabled" />
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell>{item.title}</StyledTableCell>
                                    <StyledTableCell>{item.subtitle}</StyledTableCell>
                                    <StyledTableCell>{item.createdAt ? item.createdAt.slice(0, 19) : "-"}</StyledTableCell>
                                    <StyledTableCell>{item.updatedAt ? item.updatedAt.slice(0, 19) : "-"}</StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={items.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </StyledTableContainer>
            )}
            {/* Edit/Create Modal */}
            <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="md">
                <DialogTitle>{modalMode === "edit" ? "Edit Marketing Item" : "Create Marketing Item"}</DialogTitle>
                <DialogContent>
                    {editLoading ? <CircularProgress /> : (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mt: 1 }}>
                            {modalMode === "edit" && selectedItem && (
                                <>
                                    <TextField label="ID" value={selectedItem.id} fullWidth disabled />
                                    <TextField label="Created At" value={selectedItem.createdAt || ""} fullWidth disabled />
                                    <TextField label="Updated At" value={selectedItem.updatedAt || ""} fullWidth disabled />
                                </>
                            )}

                            {/* STATE SELECTOR (AUTOCOMPLETE) */}
                            <Autocomplete
                                options={statesList}
                                getOptionLabel={(option) => option.name}
                                value={statesList.find(s => s.isoCode === selectedStateCode) || null}
                                onChange={(event, newValue) => {
                                    handleStateChange(newValue ? newValue.isoCode : null);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Filter State (To find City)" placeholder="Type to search state..." />
                                )}
                                fullWidth
                            />

                            {/* CITY SELECTOR (AUTOCOMPLETE) */}
                            <Autocomplete
                                options={cityOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                value={currentCityValue}
                                onChange={(event, newValue) => {
                                    // newValue can be null (if cleared), or a CityOption
                                    if (!newValue) {
                                        // User cleared the box
                                        handleFieldChange("city", undefined);
                                    } else {
                                        // User picked Global or a City
                                        handleFieldChange("city", newValue.value);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Target City" placeholder="Global or Search City..." />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        {option.value === null ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'green' }}>
                                                <PublicIcon fontSize="small" sx={{ mr: 1 }} />
                                                {option.label}
                                            </Box>
                                        ) : (
                                            option.label
                                        )}
                                    </li>
                                )}
                                fullWidth
                                disabled={!selectedStateCode && editData.city !== null && editData.city !== undefined && !cityOptions.some(c => c.value === editData.city)}
                            />

                            {/* Editable fields */}
                            <Button
                                component="label"
                                variant="outlined"
                                startIcon={<ImageIcon />}
                                fullWidth
                                disabled={uploading}
                                sx={{ my: 1 }}
                            >
                                {editData.imageUrl ? "Change Image" : "Upload Image"}
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleImageChange}
                                />
                            </Button>
                            {(imagePreviewUrl || editData.imageUrl) && (
                                <Box sx={{ gridColumn: "span 2" }}>
                                    {imagePreviewUrl ? (
                                        <img
                                            src={imagePreviewUrl}
                                            alt="Preview"
                                            style={{ maxWidth: "100%", maxHeight: 90, borderRadius: 10 }}
                                        />
                                    ) : (
                                        <img
                                            src={editData.imageUrl}
                                            alt="Preview"
                                            style={{ maxWidth: "100%", maxHeight: 90, borderRadius: 10 }}
                                        />
                                    )}
                                </Box>
                            )}
                            <TextField
                                label="Title"
                                value={editData.title ?? ""}
                                onChange={e => handleFieldChange("title", e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Subtitle"
                                value={editData.subtitle ?? ""}
                                onChange={e => handleFieldChange("subtitle", e.target.value)}
                                fullWidth
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDetail}>Close</Button>
                    <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading || uploading}>
                        {modalMode === "edit" ? "Save" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
            <FancyContextMenu
                actions={actions}
                open={Boolean(menu)}
                onClose={handleCloseMenu}
                anchorPosition={menu}
                title={selectedRow?.title || "Row Actions"}
            />
        </StyledPaper>
    );
};

export default MarketingList;
